/**
 * „Ustawienia rozliczeń" — the client card's `⋯` entry (T-K1): whether this client may book for
 * themselves, as the general setting says or as an exception either way (booking-system R2).
 *
 * The one per-client setting the booking controller keeps; there is no other per-client credit
 * setting on the wire. The wording is the web's own — mobile's notes run on an em-dash — except
 * the save error (`booking.trainer.selfBookingException.saveError`). The dialog says what the
 * chosen state does and links to where the setting is changed for everyone.
 *
 * ── A pick waits for „Zapisz" ─────────────────────────────────────────────────────────────────
 * The rows are native radios, so the arrows move the pick as a radio group does — and that is why
 * the write waits for the button: an arrow walk across three states must not send three writes.
 *
 * Raised from a `Menu.Item`, so Base UI returns focus to the `⋯` on close with no `finalFocus`
 * (the menu gave it back to its trigger before this opened). Focus opens on the checked radio —
 * at once when the answer is cached, else from the popup once it lands.
 */

import { Dialog } from '@base-ui/react/dialog';
import { Link } from '@tanstack/react-router';
import { useEffect, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import choice from '~/shared/ui/choiceRow.module.css';
import surface from '~/shared/ui/dialogSurface.module.css';
import form from '~/shared/ui/formDialog.module.css';
import { CloseGlyph } from '~/shared/ui/FormGlyphs';
import inlineButton from '~/shared/ui/inlineTextButton.module.css';
import skeleton from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';
import hiddenControl from '~/shared/ui/visuallyHiddenControl.module.css';

import { useSelfBooking } from '../lib/useSelfBooking';
import { SELF_BOOKING_CHOICES, selfBookingEffectKey } from '../model/selfBooking';
import styles from './SelfBookingDialog.module.css';

interface SelfBookingDialogProps {
  readonly clientId: number;
  readonly clientName: string;
  readonly open: boolean;
  readonly onClose: () => void;
  /** The card's one live region (`cardAnnouncer`): the dialog closes on the save it announces. */
  readonly announce: (message: string) => void;
}

export function SelfBookingDialog({
  clientId,
  clientName,
  open,
  onClose,
  announce,
}: SelfBookingDialogProps) {
  const { t } = useTranslation();
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop className={surface.backdrop} />
        <SelfBookingPopup
          clientId={clientId}
          clientName={clientName}
          onClose={onClose}
          onSaved={() => {
            announce(t('web:clientDetail.selfBooking.saved'));
            onClose();
          }}
        />
      </Dialog.Portal>
    </Dialog.Root>
  );
}

const WORDS = 'web:clientDetail.selfBooking';

/** The popup, and with it the reads: mounted only while the dialog is open. */
function SelfBookingPopup({
  clientId,
  clientName,
  onClose,
  onSaved,
}: {
  readonly clientId: number;
  readonly clientName: string;
  readonly onClose: () => void;
  readonly onSaved: () => void;
}) {
  const { t } = useTranslation();
  const setting = useSelfBooking(clientId);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const checkedRef = useRef<HTMLInputElement | null>(null);
  const settledFocus = useRef(false);
  const noteId = useId();
  const name = useId();
  const { content, chosen, gate } = setting;
  const ready = content.kind === 'ready';

  // Focus opens on the state the client is in (R10). A cached answer draws the radios at once and
  // `initialFocus` finds the checked one; an answer that lands later moves focus there from the
  // popup, once, and never out from under a trainer who has already moved it. „Once" is counted
  // only when focus actually reached the radio or the trainer took it elsewhere in the dialog: an
  // answer that lands before Base UI has put focus in the popup leaves the move to the popup's
  // own focus event below.
  const settleFocus = () => {
    const popup = popupRef.current;
    const active = document.activeElement;
    if (!ready || settledFocus.current || popup === null) return;
    if (active === popup) checkedRef.current?.focus();
    const now = document.activeElement;
    settledFocus.current = now !== popup && now !== null && popup.contains(now);
  };
  useEffect(settleFocus, [ready]);

  return (
    <Dialog.Popup
      ref={popupRef}
      className={`${String(surface.popup)} ${String(form.dialog)}`}
      initialFocus={() => checkedRef.current ?? popupRef.current}
      onFocus={settleFocus}
      aria-busy={gate.waiting}
    >
      <div className={form.dialogHead}>
        <div>
          <span className={form.eyebrow}>{clientName}</span>
          <Dialog.Title className={form.dialogTitle}>{t(`${WORDS}.title`)}</Dialog.Title>
        </div>
        <Dialog.Close className={form.close} aria-label={t(`${WORDS}.close`)}>
          <CloseGlyph className={form.closeGlyph} />
        </Dialog.Close>
      </div>

      <fieldset
        className={`${String(form.field)} ${String(styles.set)}`}
        aria-describedby={content.kind === 'ready' ? noteId : undefined}
      >
        <legend className={form.fieldLabel}>{t(`${WORDS}.legend`)}</legend>
        {content.kind === 'waiting' ? (
          <div className={choice.rows}>
            {SELF_BOOKING_CHOICES.map((option) => (
              <div key={option} className={choice.row} aria-hidden="true">
                {gate.skeleton ? (
                  <>
                    <i className={`${String(skeleton.bar)} ${String(styles.barMark)}`} />
                    <i className={`${String(skeleton.bar)} ${String(styles.barText)}`} />
                  </>
                ) : null}
              </div>
            ))}
            <span className={hidden.srOnly}>{t(`${WORDS}.loading`)}</span>
          </div>
        ) : content.kind === 'failed' ? (
          <div className={`${String(styles.failed)} ${gate.reveal}`} role="alert">
            <span>{t(`${WORDS}.failed`)}</span>
            <button type="button" className={surface.ghost} onClick={setting.retry}>
              {t(`${WORDS}.retry`)}
            </button>
          </div>
        ) : (
          <div className={`${String(choice.rows)} ${gate.reveal}`}>
            {SELF_BOOKING_CHOICES.map((option) => {
              const on = chosen === option;
              return (
                <label
                  key={option}
                  className={`${String(choice.row)} ${on ? String(choice.rowChosenInk) : ''}`}
                >
                  <input
                    ref={on ? checkedRef : undefined}
                    className={hiddenControl.control}
                    type="radio"
                    name={name}
                    value={option}
                    checked={on}
                    disabled={setting.saving}
                    onChange={() => {
                      setting.choose(option);
                    }}
                  />
                  <span className={choice.rowMark} aria-hidden="true" />
                  <span className={choice.rowText}>
                    <span className={choice.rowTitle}>
                      {option === 'general'
                        ? t(`${WORDS}.general`, {
                            state: t(
                              content.generalOn ? `${WORDS}.generalOn` : `${WORDS}.generalOff`,
                            ),
                          })
                        : t(`${WORDS}.${option}`)}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </fieldset>

      {/* What the chosen state does, and where it is changed for everyone. The effect reserves its
       * lines in every state, so the answer landing moves nothing. */}
      <div id={noteId} className={styles.notes}>
        <p className={`${String(form.note)} ${String(styles.note)}`}>
          {content.kind === 'ready' && chosen !== null
            ? t(selfBookingEffectKey(chosen, content.generalOn))
            : null}
        </p>
        <p className={`${String(form.note)} ${String(styles.everyone)}`}>
          {t(`${WORDS}.everyoneBefore`)}
          <Link
            className={inlineButton.inlineTextButton}
            to="/rezerwacje"
            search={{ tab: 'ustawienia' }}
          >
            {t(`${WORDS}.everyoneLink`)}
          </Link>
          {t(`${WORDS}.everyoneAfter`)}
        </p>
      </div>

      {setting.saveFailed ? (
        <p className={form.refusal} role="alert">
          {t('web:booking.trainer.selfBookingException.saveError')}
        </p>
      ) : null}

      <div className={form.dialogActs}>
        <button type="button" className={surface.ghost} onClick={onClose}>
          {t(`${WORDS}.cancel`)}
        </button>
        <button
          type="button"
          className={surface.primary}
          aria-disabled={content.kind !== 'ready' || setting.saving ? true : undefined}
          aria-busy={setting.saving}
          onClick={() => {
            setting.save(onSaved);
          }}
        >
          {setting.saving ? t(`${WORDS}.saving`) : t(`${WORDS}.save`)}
        </button>
      </div>
    </Dialog.Popup>
  );
}
